import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { TransferQueueRow } from "@care-by-ella/contracts";
import { useAuth } from "@/auth/AuthContext";
import { GAP_RISK_BADGE } from "@/lib/gap-risk-badge";
import {
  fetchTransferQueue,
  ForbiddenError,
  UnauthorizedError,
} from "@/lib/clinician-api";
import {
  completionGroupLabel,
  remainingSupplyLabel,
  transferReviewStatusLabel,
} from "@/lib/state-labels";
import { useViewPath } from "@/components/SupportView";
import { patientHref } from "@/lib/patient-links";
import {
  PATIENT_OVERVIEW_LABEL,
  patientOverviewHref,
} from "@/lib/work-item-cta";

// TES-734 — the queue "Receptövertag att granska" (Emelie 2026-09-16, A-02):
// patient, waiting time, status, owner, risk flags, missing documentation, next
// planned dose, estimated amount of medication left, prescription validity and
// risk of a gap in treatment. Open cases only; the API sorts high gap risk
// first, then longest waiting. The case itself is decided on the patient's page
// (the "Receptövertag" tab).
//
// TES-1245 (A-02) — each row leads with "Granska receptövertaget", straight
// to that tab, and "Öppna patientbilden" beside it. The name used to link
// to `/patients/:id` with no tab, which a transfer patient is redirected from
// to the same review; it now opens the overview like every patient name.

function waitedFor(iso: string, now: Date): string {
  const hours = Math.max(
    0,
    Math.floor((now.getTime() - new Date(iso).getTime()) / 3_600_000),
  );
  if (hours < 24) return `${hours} h`;
  return `${Math.floor(hours / 24)} d`;
}

// TES-892 point 3 — the column is called "Väntat sedan", so it has to name a
// point in time. It showed only an elapsed duration, which answers a
// different question ("hur länge") and leaves the doctor to do the
// subtraction in his head when he wants to know what day the patient
// actually submitted.
function waitingSinceLabel(iso: string): string {
  return new Date(iso).toLocaleString("sv-SE", {
    dateStyle: "short",
    timeStyle: "short",
  });
}

function missingLabel(item: string): string {
  if (item === "new_blood_test") return "Nytt blodprov";
  // A retired group (blood_pressure_pulse, prior_labs) can still sit on an
  // older round; completionGroupLabel knows those too.
  return completionGroupLabel(item);
}

export function TransferQueueRoute() {
  const { signOut } = useAuth();
  const viewPath = useViewPath();
  const [rows, setRows] = useState<TransferQueueRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const now = new Date();

  useEffect(() => {
    let cancelled = false;
    fetchTransferQueue()
      .then((r) => {
        if (!cancelled) setRows(r.rows);
      })
      .catch((e) => {
        if (e instanceof UnauthorizedError) {
          void signOut();
          return;
        }
        if (!cancelled) {
          setError(
            e instanceof ForbiddenError
              ? "Kön är läkarens."
              : e instanceof Error
                ? e.message
                : String(e),
          );
        }
      });
    return () => {
      cancelled = true;
    };
  }, [signOut]);

  return (
    <div className="flex flex-col gap-md">
      <div className="flex items-baseline justify-between">
        <h1 className="font-display text-3xl tracking-tight">
          Receptövertag att granska
        </h1>
        <span className="text-sm text-textSecondary">
          {rows ? `${rows.length} ärenden` : "Hämtar…"}
        </span>
      </div>
      {error ? <p className="text-danger text-sm">{error}</p> : null}
      {rows && rows.length === 0 ? (
        <p className="text-textSecondary text-sm">
          Inga öppna receptövertag just nu.
        </p>
      ) : null}
      {rows && rows.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-textSecondary uppercase tracking-wide">
                <th className="py-sm pr-md">Patient</th>
                <th className="py-sm pr-md">Väntetid</th>
                <th className="py-sm pr-md">Status</th>
                <th className="py-sm pr-md">Ansvarig</th>
                <th className="py-sm pr-md">Riskflaggor</th>
                <th className="py-sm pr-md">Saknade underlag</th>
                <th className="py-sm pr-md">Kvar</th>
                <th className="py-sm pr-md">Receptet gäller</th>
                <th className="py-sm pr-md">Glapprisk</th>
                <th className="py-sm">
                  <span className="sr-only">Åtgärder</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const status = transferReviewStatusLabel(r.status);
                return (
                  <tr
                    key={r.reviewId}
                    className="border-t border-surfaceMuted align-top"
                  >
                    <td className="py-sm pr-md">
                      <Link
                        to={viewPath(patientOverviewHref(r.patientId))}
                        className="text-brand hover:underline"
                      >
                        {r.name || "—"}
                      </Link>
                      <div className="text-xs text-textSecondary">
                        …{r.personalNumberLast6}
                      </div>
                    </td>
                    <td className="py-sm pr-md">
                      <div className="flex items-center gap-xs">
                        <span>{waitedFor(r.waitingSince, now)}</span>
                        {/* TES-892 (Ö-216) — 24 bemannade vardagstimmar. The
                            server decides it: only it knows the tenant's
                            staffed hours, and a badge computed from
                            wall-clock in the browser would light up over
                            every weekend. */}
                        {r.delayed ? (
                          <span
                            className="text-xs font-sans font-medium rounded border border-danger/40 bg-danger/10 text-danger px-xs py-[2px]"
                            title="Mer än 24 bemannade vardagstimmar utan att något hänt i ärendet"
                          >
                            Försenad
                          </span>
                        ) : null}
                      </div>
                      <div className="text-xs text-textSecondary">
                        {waitingSinceLabel(r.waitingSince)}
                      </div>
                    </td>
                    <td className="py-sm pr-md">
                      <span
                        className={`text-xs font-sans font-medium rounded border px-xs py-[2px] ${status.className}`}
                      >
                        {status.label}
                      </span>
                    </td>
                    <td className="py-sm pr-md">{r.assignee?.name ?? "—"}</td>
                    <td className="py-sm pr-md">
                      {r.openRiskFlags > 0 ? (
                        <span className="text-danger">{r.openRiskFlags}</span>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td className="py-sm pr-md">
                      {r.missing.length > 0
                        ? r.missing.map(missingLabel).join(", ")
                        : "—"}
                    </td>
                    <td className="py-sm pr-md">
                      {r.remainingSupply
                        ? remainingSupplyLabel(r.remainingSupply)
                        : "—"}
                    </td>
                    <td className="py-sm pr-md">
                      {r.prescriptionValidUntil ?? "—"}
                    </td>
                    <td className="py-sm pr-md">
                      <span
                        className={`text-xs font-sans font-medium uppercase tracking-wide rounded border px-xs py-[2px] ${GAP_RISK_BADGE[r.gapRisk.level].className}`}
                      >
                        {GAP_RISK_BADGE[r.gapRisk.level].label}
                      </span>
                    </td>
                    <td className="py-sm">
                      <div className="flex flex-wrap justify-end gap-sm">
                        <Link
                          to={viewPath(patientHref(r.patientId, "overtag"))}
                          className="whitespace-nowrap rounded-md bg-brand px-md py-sm text-sm text-white hover:bg-brand/90"
                        >
                          Granska receptövertaget
                        </Link>
                        <Link
                          to={viewPath(patientOverviewHref(r.patientId))}
                          className="whitespace-nowrap rounded-md border border-surfaceMuted px-md py-sm text-sm text-textSecondary hover:text-textPrimary"
                        >
                          {PATIENT_OVERVIEW_LABEL}
                        </Link>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
}
